import React, { useState } from "react";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { BuyCreditsDialog } from "../components/BuyCreditsDialog";
import { Button } from "../components/ui/button";
import { Check, Crown } from "lucide-react";
import { toast } from "sonner";
import { fadeUp, stagger } from "../lib/motions";

export default function Pricing() {
  const [buyOpen, setBuyOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["plans"],
    queryFn: async () => (await api.get("/plans")).data,
  });
  const plans = data?.plans || [];

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-6 md:py-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8 text-center"
        >
          <div className="text-xs uppercase tracking-[0.24em] text-cyan-400 mb-1">Pricing</div>
          <h1 data-testid="pricing-heading" className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter">
            Ship faster. Pay simply.
          </h1>
          <p className="text-zinc-400 mt-3 max-w-xl mx-auto">
            All plans include multi-reference, character consistency, and access to available models. Upgrade any time.
          </p>
        </motion.div>

        <motion.div initial="hidden" animate="show" variants={stagger(0.1, 0.1)} className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {plans.map((p) => (
            <motion.div
              key={p.id}
              variants={fadeUp}
              whileHover={{ y: -4 }}
              transition={{ duration: 0.2 }}
              data-testid={`plans-page-${p.id}`}
              className={`relative p-7 rounded-2xl bg-white/[0.02] border ${
                p.featured ? "border-fuchsia-500/50 md:scale-[1.02]" : "border-white/10 hover:border-white/20"
              } transition-colors duration-200`}
            >
              {p.featured && (
                <div className="absolute -top-3 left-6 text-[10px] uppercase tracking-[0.24em] px-3 py-1 rounded-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white font-semibold flex items-center gap-1">
                  <Crown className="w-3 h-3" /> Popular
                </div>
              )}
              <div className="text-xs uppercase tracking-widest text-zinc-500">{p.tagline}</div>
              <div className="font-display font-extrabold text-2xl mt-1">{p.name}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-5xl font-black tracking-tighter">${p.price_monthly}</span>
                <span className="text-zinc-500 text-sm">/mo</span>
              </div>
              <div className="text-xs text-zinc-500 mt-1">{p.credits_monthly} credits / month</div>
              <Button
                data-testid={`plan-select-${p.id}`}
                onClick={() => setBuyOpen(true)}
                className={`w-full mt-6 rounded-full h-11 font-semibold ${p.featured ? "btn-primary" : "bg-white/8 border border-white/15 hover:bg-white/12 text-white"}`}
              >
                {p.cta}
              </Button>
              <ul className="mt-6 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                    <Check className="w-4 h-4 mt-0.5 text-cyan-400 shrink-0" strokeWidth={2.5} />
                    {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <BuyCreditsDialog
        open={buyOpen}
        onOpenChange={setBuyOpen}
        reason="Choose a plan and message us on WhatsApp to activate it."
      />
    </AppShell>
  );
}
